import { FunctionComponent } from "react";
import { Field } from "react-final-form";

import { DateTime } from "@bps/date-time";
import { Frequency } from "@libs/gateways/booking/BookingGateway.dtos.ts";
import { toRecurrenceOptions } from "@libs/utils/calendar/calendar.utils.ts";
import { nameOfFactory } from "@libs/utils/name-of.utils.ts";
import { getFrequencyKeyTextOptions } from "@modules/settings/screens/users/components/utils.ts";
import { DatePickerField } from "@ui-components/form/DatePickerField.tsx";
import { DropdownField } from "@ui-components/form/DropdownField.tsx";
import { FieldCondition } from "@ui-components/form/FieldCondition.tsx";
import { Fieldset } from "@ui-components/form/Fieldset/Fieldset.tsx";
import { SpinNumberInputField } from "@ui-components/form/SpinNumberInputField.tsx";
import { TextInputField } from "@ui-components/form/TextInputField.tsx";
import { TimePickerField } from "@ui-components/form/TimePickerField.tsx";
import { WeekdaysDropdownField } from "@ui-components/form/WeekdaysDropdownField.tsx";

import { isAfter, isMonth, isOnDate, isWeek, isYear } from "../../utils.tsx";
import {
  RecurrenceFormLabels,
  RecurrenceFormValues
} from "./RecurrenceForm.types.tsx";

const nameOf = nameOfFactory<RecurrenceFormValues>();

export const RecurrenceFormFields: FunctionComponent = () => {
  return (
    <Fieldset>
      <TextInputField
        label={RecurrenceFormLabels.purpose}
        name={nameOf("purpose")}
      />

      <Fieldset horizontal>
        <DatePickerField
          label={RecurrenceFormLabels.startSchedule}
          name={nameOf("startDate")}
        />
        <DropdownField
          label={RecurrenceFormLabels.endSchedule}
          styles={{ root: { flexGrow: 1 } }}
          name={nameOf("endScheduleType")}
          options={[
            { key: "ondate", text: "On date" },
            { key: "after", text: "After" },
            { key: "never", text: "Never" }
          ]}
        />
        <FieldCondition when={nameOf("endScheduleType")} is={isOnDate}>
          <DatePickerField
            styles={{ root: { flexGrow: 1 } }}
            placeholder="Until"
            name={nameOf("until")}
            label="&nbsp;"
          />
        </FieldCondition>
        <FieldCondition when={nameOf("endScheduleType")} is={isAfter}>
          <Field<RecurrenceFormValues["count"]>
            name={nameOf("count")}
            subscription={{ value: true }}
          >
            {({ input }) => (
              <SpinNumberInputField
                label={
                  (input.value ?? 0) > 1
                    ? `${RecurrenceFormLabels.occurrence}s`
                    : RecurrenceFormLabels.occurrence
                }
                name={nameOf("count")}
                min={1}
                max={99}
                step={1}
                fieldItemStyles={{
                  itemWrapper: {
                    alignItems: "baseline"
                  },
                  item: { flexGrow: 0 }
                }}
                styles={{ root: { width: 120 } }}
              />
            )}
          </Field>
        </FieldCondition>
      </Fieldset>

      <Fieldset horizontal>
        <TimePickerField
          label={RecurrenceFormLabels.startTime}
          suggestionInterval={30}
          name={nameOf("startTime")}
        />
        <TimePickerField
          label={RecurrenceFormLabels.endTime}
          suggestionInterval={30}
          name={nameOf("endTime")}
        />

        <SpinNumberInputField
          label={RecurrenceFormLabels.recurEvery}
          name={nameOf("interval")}
          min={0}
          max={99}
          step={1}
          fieldItemStyles={{
            itemWrapper: { alignItems: "baseline" },
            item: { flexGrow: 0 }
          }}
          // SpinNumberInput component has a min width of 86
          styles={{ root: { width: 86 } }}
        />

        <Field<RecurrenceFormValues["interval"]>
          name={nameOf("interval")}
          subscription={{ value: true }}
        >
          {({ input }) => (
            <DropdownField
              styles={{ root: { flexGrow: 1 } }}
              label="&nbsp;"
              name={nameOf("frequency")}
              options={getFrequencyKeyTextOptions(input.value)}
            />
          )}
        </Field>
      </Fieldset>
      <Fieldset>
        <FieldCondition when={nameOf("frequency")} is={isWeek}>
          <WeekdaysDropdownField
            styles={{ root: { flexGrow: 1 } }}
            required
            name={nameOf("dayRecur")}
            label={RecurrenceFormLabels.on}
          />
        </FieldCondition>

        <FieldCondition when={nameOf("frequency")} is={isMonth}>
          <Field<RecurrenceFormValues["startDate"]>
            name={nameOf("startDate")}
            subscription={{ value: true }}
          >
            {({ input }) => (
              <DropdownField
                styles={{ root: { flexGrow: 1 } }}
                required
                label="On"
                name={nameOf("monthYearRecurrence")}
                options={toRecurrenceOptions(
                  DateTime.fromJSDate(input.value),
                  Frequency.Month
                )}
              />
            )}
          </Field>
        </FieldCondition>
        <FieldCondition when={nameOf("frequency")} is={isYear}>
          <Field<RecurrenceFormValues["startDate"]>
            name={nameOf("startDate")}
            subscription={{ value: true }}
          >
            {({ input }) => (
              <DropdownField
                styles={{ root: { flexGrow: 1 } }}
                required
                label="On"
                name={nameOf("monthYearRecurrence")}
                options={toRecurrenceOptions(
                  DateTime.fromJSDate(input.value),
                  Frequency.Year
                )}
              />
            )}
          </Field>
        </FieldCondition>
      </Fieldset>
    </Fieldset>
  );
};
